import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Button, Link } from '@resy/ui-kitchen';
import { parameterize } from '../../../../../../ui-kitchen/src/utils';
import { handleMktAnalytics } from '../../AmexMarketingUtils/AmexUtils';
import {
  AMEX_DELTA_UTM_MEDIUM, AMEX_UTM_SOURCE, CTA_EVENT_NAME, AMEX_DELTA_UTM_EDITORIAL_CAMPAIGN,
} from '../../AmexMarketingUtils/constants';
import './DeltaWifiIntroBanner.scss';

const DeltaWifiIntroBanner = (
  {
    baseRequestUrl,
    editorialContent,
    introConfig,
    isAgnostic,
    isAuthenticated,
    showSignupModal,
    login,
    slug,
    analyticsEvent,
  }
) => {
  const marketOrAgnosticIntroData = isAgnostic ? introConfig?.agnostic : introConfig?.market;
  const leftBoxData = isAuthenticated ? marketOrAgnosticIntroData?.logged_in : marketOrAgnosticIntroData?.logged_out;
  const folderName = isAgnostic ? 'agnostic' : 'market';

  const displayCTA = (cta) => {
    const { label, url } = cta;
    const query = {
      ...(!isAgnostic && slug) && {
        city: slug,
      },
      utm: {
        utm_source: AMEX_UTM_SOURCE,
        utm_medium: AMEX_DELTA_UTM_MEDIUM,
        utm_campaign: 'email-opt-in',
      },
    };

    switch (cta.name) {
      case 'optIn':
        return (
          <Link
            className="DeltaWifiIntroBanner__section--primary"
            href={{ pathname: url, query }}
            target="_blank"
            onClick={() => handleMktAnalytics(analyticsEvent, CTA_EVENT_NAME, { action_name: 'Email Opt In - Click' })}
          >
            {label}
          </Link>
        );
      case 'login':
        return (
          <Button
            variant="no-style"
            className="DeltaWifiIntroBanner__section--outline"
            onClick={() => {
              handleMktAnalytics(analyticsEvent, CTA_EVENT_NAME, { action_name: 'What is Resy - Login Click' }, login);
            }}
          >
            {label}
          </Button>
        );
      case 'account':
        return (
          <Button
            variant="primary"
            type="button"
            className="DeltaWifiIntroBanner__section--primary"
            onClick={() => { handleMktAnalytics(analyticsEvent, CTA_EVENT_NAME, { action_name: 'What is Resy - Create an Account Click' }, showSignupModal); }}
            data-link="create-account"
          >
            {label}
          </Button>
        );
      default:
        return null;
    }
  };

  const displayLeftBox = (data) => (
    <div className="col-md-6">
      <div className="DeltaWifiIntroBanner__section">
        <h2 className="DeltaWifiIntroBanner__section--title">{data.title}</h2>
        <h3 className="DeltaWifiIntroBanner__section--subtitle">{data.subtitle}</h3>
        <p className="DeltaWifiIntroBanner__section--text">{data.text}</p>
        <div className={cx('DeltaWifiIntroBanner__section__button-container', {
          single: ((data.ctas && data.ctas.length === 1)),
        })}
        >
          {(data.ctas && data.ctas.length > 0) && data.ctas.map((cta) => displayCTA(cta))}
        </div>
      </div>
    </div>
  );

  const displayHitList = (data) => {
    const {
      hitListUrl, title, subtitle, image, text, readMoreUrl, readMoreText,
    } = data;

    const editorialUtm = {
      utm_source: AMEX_UTM_SOURCE,
      utm_medium: AMEX_DELTA_UTM_MEDIUM,
      utm_campaign: AMEX_DELTA_UTM_EDITORIAL_CAMPAIGN,
    };
    const hrefValue = `${hitListUrl}&${parameterize(editorialUtm)}`;
    return (
      <div className="col-md-6 DeltaWifiIntroBanner__vertical-space">
        <div className="DeltaWifiIntroBanner__section">
          <h2 className="DeltaWifiIntroBanner__section--title">
            <Link
              className="DeltaWifiIntroBanner__section--title-link"
              href={hrefValue}
              target="_blank"
              onClick={() => handleMktAnalytics(analyticsEvent, CTA_EVENT_NAME, { action_name: 'Hitlist - Title Link Click' })}
            >
              {title}
            </Link>
          </h2>
          <h3
            id="hitListSubtitle"
            className="DeltaWifiIntroBanner__section--subtitle"
          >
            {subtitle}
          </h3>
          <div className="DeltaWifiIntroBanner__section--withimage">
            <img
              src={`${baseRequestUrl}DeltaWifi/editorialData/${folderName}/images/${image}`}
              alt=""
            />
            <div className="DeltaWifiIntroBanner__section--withimage-body">
              <p className="DeltaWifiIntroBanner__section--text">{text}</p>
              <p className="DeltaWifiIntroBanner__section--withlink">
                <Link
                  className="DeltaWifiIntroBanner__section--link"
                  href={{
                    pathname: readMoreUrl,
                    query: editorialUtm,
                  }}
                  aria-describedby="hitListSubtitle"
                  target="_blank"
                  onClick={() => handleMktAnalytics(analyticsEvent, CTA_EVENT_NAME, { action_name: 'Hitlist - Read More Click' })}
                >
                  {readMoreText}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="DeltaWifiIntroBanner">
      <div className="container">
        <div className="row">
          {displayLeftBox(leftBoxData)}
          {displayHitList(editorialContent)}
        </div>
      </div>
    </section>
  );
};

DeltaWifiIntroBanner.propTypes = {
  baseRequestUrl: PropTypes.string.isRequired,
  editorialContent: PropTypes.shape({}).isRequired,
  isAgnostic: PropTypes.bool.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  login: PropTypes.func.isRequired,
  showSignupModal: PropTypes.func.isRequired,
  introConfig: PropTypes.arrayOf({
    agnostic: PropTypes.shape({
      loggedIn: PropTypes.shape({}),
      loggedOut: PropTypes.shape({}),
    }),
    market: PropTypes.shape({
      loggedIn: PropTypes.shape({}),
      loggedOut: PropTypes.shape({}),
    }),
  }).isRequired,
  slug: PropTypes.string.isRequired,
  analyticsEvent: PropTypes.func.isRequired,
};

export default DeltaWifiIntroBanner;
